import React from "react";
import PropTypes from "prop-types";
import { Col, Row, Button, Tooltip } from "antd";
import CustomIcon from "../CustomIcon/CustomIcon";
import "./PropertyCountBox.scss";

const PropertyCountBox = ({ title, properties, actionText, disabledReason }) => (
  <div className="pl-stats-card">
    <h3 className="pl-stat__label">{title}</h3>
    {properties.map(({ name, total, percentage, type, redirectToPublish, actionText: itemActionText, disabledReason: itemDisabledReason },
      index,
    ) => {
      const isDisabled = total === 0;
      const disabledReasonText = itemDisabledReason || disabledReason || "No marketable units";
      const actionLabel = itemActionText || actionText;
      const tooltipId = `pl-publish-tooltip-${type || index}`;

      const handleActivate = e => {
        if (isDisabled) return;
        if (e.type === "click") {
          redirectToPublish(type);
          return;
        }
        if (e.type === "keydown" && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          redirectToPublish(type);
        }
      };

      return (
        <Row key={name} className="pl-stat__property_container">
          <Col flex="1 0%">
            <span className="pl-stat__name">{name}</span>
          </Col>
          <Col flex="1 0%">
            <Row>
              <Col flex="1 0%">
                <div className="pl-stat__metric">
                  <span className="pl-stat__value pl-stat-total">{total}</span>
                  {percentage ? (
                    <>
                      <span className="pl-stat__sep" aria-hidden="true" />
                      <span className="pl-stat__pct pl-stat-total">{percentage}</span>
                    </>
                  ) : null}
                </div>
              </Col>
              <Col flex="1 0%">
                <div className="pl-stat__action-container">
                  {/* Wrap with a focusable container so Tooltip can trigger on focus even if "disabled" */}
                  <Tooltip title={isDisabled ? disabledReasonText : ""} placement="top" trigger={["hover", "focus"]}>
                    {/*
                      Don't use disabled. Keep it in the tab order.
                      Use aria-disabled + aria-describedby so SRs announce the reason.
                    */}
                    <Button
                      type="link"
                      className={isDisabled ? "pl-action--disabled" : "pl-action--enabled"}
                      aria-disabled={isDisabled ? "true" : "false"}
                      aria-describedby={isDisabled ? tooltipId : undefined}
                      role="button"
                      tabIndex={0}
                      onClick={handleActivate}
                      onKeyDown={handleActivate}
                    >
                      <span
                        className={`pl-stat__action ${
                          isDisabled ? "pl-action-button-disabled" : "pl-action-button-enabled"
                        }`}
                      >
                        {actionLabel}
                      </span>
                      <CustomIcon
                        size={25}
                        className="pl-stat__arrow-icon"
                        name={isDisabled ? "arrowRightLongGray" : "arrowRightLongBlue"}
                      />
                    </Button>
                  </Tooltip>

                  {/* Screen-reader-only description that explains why it's disabled */}
                  {isDisabled && (
                    <span id={tooltipId} className="sr-only">
                      {disabledReasonText}
                    </span>
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      );
    })}
  </div>
);

PropertyCountBox.propTypes = {
  title: PropTypes.string.isRequired,
  actionText: PropTypes.string,
  disabledReason: PropTypes.string,
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
      percentage: PropTypes.string,
      type: PropTypes.string,
      redirectToPublish: PropTypes.func.isRequired,
      actionText: PropTypes.string,
      disabledReason: PropTypes.string,
    }),
  ).isRequired,
};

export default PropertyCountBox;
